import { AnalyzedSegment, AnalyzerTemplate, DiffToken, PromptAnalysis, SideBySideDiff } from './types'

/** Max characters a {{variable}} may stand for when matching a template inside a pasted prompt. */
const MAX_VARIABLE_LENGTH = 2000
/** A pasted paragraph is attributed to a template when at least this share of its word pairs appear in it. */
const SIMILAR_CONTAINMENT_THRESHOLD = 0.5
/** Word-diff is skipped above this many LCS cells to keep the UI responsive. */
const MAX_DIFF_CELLS = 2_000_000

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Builds a regex for a template's content: whitespace runs match any whitespace,
 * and each {{variable}} matches any text (bounded). Returns null for templates with no literal text.
 */
export function templateToRegex(content: string): RegExp | null {
  const trimmed = content.trim()
  if (!trimmed) return null
  const pieces = trimmed.split(/\{\{\w+\}\}/)
  if (!pieces.some((p) => p.trim())) return null
  const literal = (p: string) => p.split(/\s+/).map(escapeRegex).join('\\s+')
  const body = pieces.map(literal).join(`[\\s\\S]{0,${MAX_VARIABLE_LENGTH}}?`)
  return new RegExp(body, 'g')
}

function words(text: string): string[] {
  return text.toLowerCase().split(/\s+/).filter(Boolean)
}

/** Word bigrams (or single words for very short text) as a multiset. */
function shingles(text: string): Map<string, number> {
  const w = words(text)
  const grams = w.length < 3 ? w : w.slice(0, -1).map((x, i) => `${x} ${w[i + 1]}`)
  const m = new Map<string, number>()
  for (const g of grams) m.set(g, (m.get(g) ?? 0) + 1)
  return m
}

function sizeOf(m: Map<string, number>): number {
  let n = 0
  for (const v of m.values()) n += v
  return n
}

function overlap(a: Map<string, number>, b: Map<string, number>): number {
  let n = 0
  for (const [k, v] of a) n += Math.min(v, b.get(k) ?? 0)
  return n
}

/** Share of `part`'s word pairs that also appear in `whole` (0-1). */
function containment(part: Map<string, number>, whole: Map<string, number>): number {
  const size = sizeOf(part)
  return size === 0 ? 0 : overlap(part, whole) / size
}

/** Dice similarity between two texts' word pairs (0-1). */
export function similarity(a: string, b: string): number {
  const sa = shingles(a)
  const sb = shingles(b)
  const total = sizeOf(sa) + sizeOf(sb)
  return total === 0 ? 0 : (2 * overlap(sa, sb)) / total
}

interface Span { start: number; end: number; key: string }

/** Exact (whitespace/variable-tolerant) template occurrences, longest first, without overlaps. */
function findExactMatches(text: string, templates: AnalyzerTemplate[]): Span[] {
  const candidates: Span[] = []
  for (const tpl of templates) {
    const re = templateToRegex(tpl.content)
    if (!re) continue
    for (const m of text.matchAll(re)) {
      if (m[0].length > 0) candidates.push({ start: m.index!, end: m.index! + m[0].length, key: tpl.key })
    }
  }
  candidates.sort((a, b) => (b.end - b.start) - (a.end - a.start))
  const taken: Span[] = []
  for (const c of candidates) {
    if (!taken.some((t) => c.start < t.end && t.start < c.end)) taken.push(c)
  }
  return taken.sort((a, b) => a.start - b.start)
}

/**
 * Splits unrecognized text into paragraphs, attributes each to its closest template (if similar enough),
 * and merges consecutive paragraphs attributed to the same template.
 */
function classifyGap(gap: string, templates: AnalyzerTemplate[], templateShingles: Map<string, Map<string, number>>): AnalyzedSegment[] {
  const paragraphs = gap.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean)
  const out: AnalyzedSegment[] = []
  for (const para of paragraphs) {
    const ps = shingles(para)
    let best: AnalyzerTemplate | null = null
    let bestScore = 0
    for (const tpl of templates) {
      const score = containment(ps, templateShingles.get(tpl.key)!)
      if (score > bestScore) { bestScore = score; best = tpl }
    }
    const last = out[out.length - 1]
    if (best && bestScore >= SIMILAR_CONTAINMENT_THRESHOLD) {
      if (last?.kind === 'similar' && last.key === best.key) {
        last.text += `\n\n${para}`
      } else {
        out.push({ kind: 'similar', text: para, key: best.key, label: best.label, similarity: 0 })
      }
    } else if (last?.kind === 'unknown') {
      last.text += `\n\n${para}`
    } else {
      out.push({ kind: 'unknown', text: para })
    }
  }
  for (const seg of out) {
    if (seg.kind === 'similar') {
      const tpl = templates.find((t) => t.key === seg.key)!
      seg.similarity = similarity(seg.text, tpl.content)
    }
  }
  return out
}

/** Decomposes a pasted full prompt into known templates, near-matches, and unknown text. */
export function analyzePrompt(text: string, allTemplates: AnalyzerTemplate[]): PromptAnalysis {
  const templates = allTemplates.filter((t) => t.content.trim())
  const templateShingles = new Map(templates.map((t) => [t.key, shingles(t.content)]))
  const labelOf = new Map(templates.map((t) => [t.key, t.label]))
  const segments: AnalyzedSegment[] = []

  let pos = 0
  const pushGap = (end: number) => {
    const gap = text.slice(pos, end)
    if (gap.trim()) segments.push(...classifyGap(gap, templates, templateShingles))
  }
  for (const span of findExactMatches(text, templates)) {
    pushGap(span.start)
    segments.push({ kind: 'match', text: text.slice(span.start, span.end), key: span.key, label: labelOf.get(span.key)! })
    pos = span.end
  }
  pushGap(text.length)

  return {
    segments,
    matchedCount: segments.filter((s) => s.kind === 'match').length,
    similarCount: segments.filter((s) => s.kind === 'similar').length,
    unknownChars: segments.filter((s) => s.kind === 'unknown').reduce((n, s) => n + s.text.length, 0),
  }
}

/** Words with their trailing whitespace, so a diff can be rendered with the original line breaks. */
function wordTokens(text: string): { word: string; raw: string }[] {
  return (text.trim().match(/\S+\s*/g) ?? []).map((raw) => ({ word: raw.trim(), raw }))
}

/**
 * Side-by-side word diff: `left` is the saved template (same + removed words),
 * `right` is the analyzed text (same + added words). Original whitespace is preserved.
 * Null when the texts are too large to diff.
 */
export function sideBySideDiff(before: string, after: string): SideBySideDiff | null {
  const a = wordTokens(before)
  const b = wordTokens(after)
  if (a.length * b.length > MAX_DIFF_CELLS) return null

  // LCS table, built from the end so the walk below goes forward
  const lcs: Uint32Array[] = Array.from({ length: a.length + 1 }, () => new Uint32Array(b.length + 1))
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lcs[i][j] = a[i].word === b[j].word ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1])
    }
  }

  const left: DiffToken[] = []
  const right: DiffToken[] = []
  const push = (side: DiffToken[], text: string, type: DiffToken['type']) => {
    const last = side[side.length - 1]
    if (last?.type === type) last.text += text
    else side.push({ text, type })
  }
  let i = 0
  let j = 0
  while (i < a.length && j < b.length) {
    if (a[i].word === b[j].word) { push(left, a[i++].raw, 'same'); push(right, b[j++].raw, 'same') }
    else if (lcs[i + 1][j] >= lcs[i][j + 1]) push(left, a[i++].raw, 'removed')
    else push(right, b[j++].raw, 'added')
  }
  while (i < a.length) push(left, a[i++].raw, 'removed')
  while (j < b.length) push(right, b[j++].raw, 'added')
  return { left, right }
}
